import { sessionKey } from './journal.js';
export const IDLE_SESSION_OBSERVATION = Object.freeze({ status: 'idle' });
/** Schedules observation reads; only the Session consumer can commit recovered authority. */
export function createSessionObservationCoordinator(options) {
    const observations = new Map();
    const listeners = new Set();
    let closed = false;
    const notify = () => {
        for (const listener of listeners) {
            try {
                listener();
            }
            catch { /* Reader failures cannot interrupt observation settlement. */ }
        }
    };
    const identity = (scope) => {
        const view = options.getView(scope);
        return JSON.stringify([view?.observationGeneration ?? 0, view?.observationAvailability?.revision ?? null, scope.bindingId ?? view?.sessionControlBindingId ?? null]);
    };
    const observation = (scope) => {
        const key = sessionKey(scope);
        let value = observations.get(key);
        if (!value && !closed) {
            value = { scope: { ...scope }, attached: 0, available: false, pending: new Map() };
            observations.set(key, value);
        }
        return value;
    };
    const current = (value) => !closed && observations.get(sessionKey(value.scope)) === value;
    const start = (value) => {
        const id = identity(value.scope);
        const existing = value.pending.get(id);
        if (existing)
            return existing;
        value.attempted = id;
        value.state = { identity: id, value: { status: 'synchronizing' } };
        // Register before invoking the adapter: consumer publication may synchronously reenter refresh.
        let resolve, reject;
        const pending = new Promise((yes, no) => { resolve = yes; reject = no; });
        value.pending.set(id, pending);
        notify();
        const finish = (state) => {
            value.pending.delete(id);
            if (!current(value))
                return;
            if (id === identity(value.scope))
                value.state = { identity: id, value: state };
            notify();
        };
        try {
            void options.synchronize(value.scope).then(result => {
                const error = options.failureReason?.(result);
                finish(error ? { status: 'failed', error } : IDLE_SESSION_OBSERVATION);
                resolve(result);
            }).catch(error => {
                finish({ status: 'failed', error: error instanceof Error ? error.message : 'session_observation_unconfirmed' });
                reject(error);
            });
        }
        catch (error) {
            finish({ status: 'failed', error: error instanceof Error ? error.message : 'session_observation_unconfirmed' });
            reject(error);
        }
        return pending;
    };
    const refreshOne = (value, epoch = value.epoch) => {
        if (!current(value) || !options.isAvailable(value.scope))
            return;
        const view = options.getView(value.scope);
        if (!view || view.loading || !view.loaded || view.storageError?.length || view.observationAvailability && view.observationAvailability.status !== 'available')
            return;
        const id = identity(value.scope);
        const bootstrap = (value.attached > 0 && !!value.scope.bindingId || value.available) && value.attempted !== id;
        const gap = epoch && options.needsTimelineSync(value.scope)
            && (value.attemptedGap !== undefined || [...observations.values()].filter(entry => entry.attemptedGap !== undefined).length < 128)
            ? JSON.stringify([id, epoch]) : undefined;
        if (!bootstrap && (!gap || value.attemptedGap === gap))
            return;
        if (gap)
            value.attemptedGap = gap;
        void start(value).catch(() => { });
    };
    return {
        attach(scope) {
            const value = observation(scope);
            if (!value)
                return () => { };
            value.attached++;
            refreshOne(value);
            return () => { value.attached = Math.max(0, value.attached - 1); };
        },
        availability(scope) {
            const value = observation(scope);
            if (!value)
                return;
            value.available = options.getView(scope)?.observationAvailability?.status === 'available';
            refreshOne(value);
        },
        gap(scope, epoch) {
            const key = sessionKey(scope), existing = observations.get(key);
            const watch = existing?.epoch !== undefined || [...observations.values()].filter(value => value.epoch !== undefined).length < 128;
            if (!watch && !options.needsTimelineSync(scope))
                return;
            const value = observation(scope);
            if (!value)
                return;
            if (watch)
                value.epoch = epoch;
            refreshOne(value, epoch);
            // Overflow scopes retain only admitted attempts, never additional refresh watches.
            if (!existing && value.epoch === undefined && value.attemptedGap === undefined)
                observations.delete(key);
        },
        retry(scope) {
            const value = observation(scope);
            if (!value || !options.isAvailable(scope)) {
                if (value) {
                    value.state = { identity: identity(scope), value: { status: 'failed', error: 'session_observation_unavailable' } };
                    notify();
                }
                return Promise.reject(new Error('session_observation_unavailable'));
            }
            return start(value);
        },
        refresh() { for (const value of observations.values())
            refreshOne(value); notify(); },
        snapshot(scope) {
            const state = observations.get(sessionKey(scope))?.state;
            return state?.identity === identity(scope) ? state.value : IDLE_SESSION_OBSERVATION;
        },
        subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        releaseChannel(channelId) {
            for (const [key, value] of observations)
                if (value.scope.channelId === channelId)
                    observations.delete(key);
            notify();
        },
        close() { closed = true; observations.clear(); notify(); listeners.clear(); },
    };
}
//# sourceMappingURL=session-observation-coordinator.js.map